import { memo, useEffect, useRef } from 'react';
import { X, Trash2, Package } from 'lucide-react';
import { useCompare } from '../contexts/CompareContext';
import { useCompare as useCompareQuery } from '../hooks/useProducts';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../config/translations';
import CompareTable from './CompareTable';
import ShareButton from './ShareButton';

const CompareDrawer = memo(function CompareDrawer() {
  const { productIds, isDrawerOpen, closeDrawer, clearCompare, removeFromCompare } = useCompare();
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);

  const { data: compareData, isLoading, error } = useCompareQuery(productIds);

  // Absolute URL of the standalone, shareable comparison page (/compare/:ids).
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  // Accessibility: CompareDrawer declares role="dialog" aria-modal="true", so it must
  // follow the WAI-ARIA modal dialog pattern — close on Escape, keep Tab focus within
  // the dialog, move focus into the dialog on open, and restore focus to the trigger
  // (e.g. the CompareBar "Compare" button) on close. Without this, keyboard and screen
  // reader users had no way to dismiss the dialog except tabbing to the close button,
  // and Tab could escape onto the hidden page behind the backdrop.
  useEffect(() => {
    if (!isDrawerOpen) return undefined;

    // Remember the element that held focus before the dialog opened so focus can be
    // returned there when it closes.
    const activeEl = document.activeElement;
    if (activeEl && dialogRef.current && !dialogRef.current.contains(activeEl)) {
      previouslyFocusedRef.current = activeEl;
    }

    // Land keyboard focus inside the dialog rather than on the page behind the backdrop.
    closeButtonRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeDrawer();
        return;
      }
      // Basic focus trap: cycle Tab/Shift+Tab within the dialog's focusable elements.
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === 'function') {
        prev.focus();
      }
      previouslyFocusedRef.current = null;
    };
  }, [isDrawerOpen, closeDrawer]);

  if (!isDrawerOpen) return null;

  // The compare endpoint wraps its payload in { success, data: { products, ... }, timestamp }.
  // Fall back to the unwrapped shape defensively.
  const comparison = compareData?.data || compareData || {};
  const products = comparison.products || [];

  // Accessibility: announce drawer open / changes
  const drawerAnnouncement = productIds.length === 0
    ? t('selectProductsToCompare')
    : productIds.length < 2
    ? t('selectAtLeastTwo')
    : `${t('compareProducts')} ${productIds.length} ${productIds.length === 1 ? t('product') : t('products')}`;
  const commonSpecs = comparison.commonSpecs || [];
  const diffSpecs = comparison.diffSpecs || [];

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={closeDrawer}
        aria-hidden="true"
      />

      {/* Drawer - Accessibility: added role="dialog", aria-modal, aria-labelledby */}
      <div
        ref={dialogRef}
        className="absolute right-0 top-0 h-full w-full max-w-4xl bg-white shadow-xl flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="compare-drawer-title"
        aria-live="polite"
        aria-atomic="true"
      >
        <span className="sr-only">{drawerAnnouncement}</span>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 id="compare-drawer-title" className="text-lg font-semibold text-gray-900">
            {t('compareProducts')} ({productIds.length})
          </h2>
          <div className="flex items-center gap-2">
            {productIds.length > 0 && (
              <button
                onClick={clearCompare}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                aria-label={t('clearAllCompare') || 'Clear all products from comparison'}
              >
                <Trash2 className="w-4 h-4" aria-hidden="true" />
                <span>{t('clearAll')}</span>
              </button>
            )}
            <button
              ref={closeButtonRef}
              onClick={closeDrawer}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label={t('close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {productIds.length < 2 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Package className="w-16 h-16 text-gray-300 mb-4" aria-hidden="true" />
              <p className="text-gray-500 mb-2">{t('selectProductsToCompare')}</p>
              <p className="text-sm text-gray-600">
                {t('selectAtLeastTwo')}
              </p>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center h-full" role="status" aria-label="Loading comparison data">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" aria-hidden="true" />
              <span className="sr-only">{t('loading') || 'Loading...'}</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <p className="text-red-500">{t('errorLoadingCompare')}</p>
            </div>
          ) : (
            <CompareTable
              products={products}
              commonSpecs={commonSpecs}
              diffSpecs={diffSpecs}
              onRemoveProduct={removeFromCompare}
              onProductNavigate={closeDrawer}
            />
          )}
        </div>

        {/* Footer */}
        {productIds.length >= 2 && (
          <div className="border-t p-4 bg-gray-50 flex flex-col items-center gap-3">
            <p className="text-xs text-gray-500 text-center">
              {t('compareHint')}
            </p>
            <ShareButton
              url={`${origin}/compare/${productIds.join(',')}`}
              title={t('compareProducts')}
            />
          </div>
        )}
      </div>
    </div>
  );
});

export default CompareDrawer;
